// Comprehensive on-page internal sitemap — improves crawl depth & PageRank flow.
// Lazy-friendly (pure static markup), no images, server-rendered.
import { Link } from "@tanstack/react-router";
import { Compass, Languages, MapPin, Stamp, FileCheck2, Plane, Building2, BookOpen, Award, HelpCircle, FileText, Heart } from "lucide-react";

type Group = {
  icon: typeof Compass;
  title: string;
  links: Array<{ to: string; label: string }>;
};

const GROUPS: Group[] = [
  {
    icon: Languages, title: "แปลเอกสาร 50+ ภาษา",
    links: [
      { to: "/translate", label: "ภาษาทั้งหมด" },
      { to: "/services/translation", label: "บริการแปลรับรอง" },
      { to: "/translate/english", label: "ภาษาอังกฤษ" },
      { to: "/translate/chinese", label: "ภาษาจีน" },
      { to: "/translate/japanese", label: "ภาษาญี่ปุ่น" },
      { to: "/translate/korean", label: "ภาษาเกาหลี" },
      { to: "/translate/arabic", label: "ภาษาอาหรับ" },
    ],
  },
  {
    icon: Stamp, title: "Apostille / Legalization 50+ ประเทศ",
    links: [
      { to: "/apostille", label: "ภาพรวม Apostille" },
      { to: "/embassy", label: "รับรองสถานทูต" },
      { to: "/apostille/usa", label: "Apostille USA" },
      { to: "/apostille/uk", label: "Apostille UK" },
      { to: "/apostille/germany", label: "Apostille เยอรมัน" },
      { to: "/apostille/australia", label: "Apostille ออสเตรเลีย" },
      { to: "/notary", label: "Notary Public 6 ทนาย" },
    ],
  },
  {
    icon: Plane, title: "วีซ่า · Checklist · เปรียบเทียบ",
    links: [
      { to: "/visa", label: "วีซ่าทั้งหมด" },
      { to: "/visa-comparison", label: "เปรียบเทียบ 10 ประเภท" },
      { to: "/checklist", label: "Checklist เอกสาร" },
      { to: "/visa/american", label: "ชาวอเมริกัน" },
      { to: "/visa/british", label: "ชาวอังกฤษ" },
      { to: "/visa/chinese", label: "ชาวจีน" },
      { to: "/marriage", label: "จดทะเบียนสมรสคนต่างชาติ" },
    ],
  },
  {
    icon: MapPin, title: "พื้นที่ให้บริการ 50 เขต + 77 จังหวัด",
    links: [
      { to: "/locations", label: "พื้นที่ทั้งหมด" },
      { to: "/near", label: "ใกล้ฉัน · Hyperlocal" },
      { to: "/locations/bts-asok", label: "BTS อโศก / สุขุมวิท" },
      { to: "/locations/bts-sala-daeng", label: "BTS ศาลาแดง / สีลม" },
      { to: "/locations/bts-phrom-phong", label: "BTS พร้อมพงษ์" },
      { to: "/locations/mrt-lat-phrao", label: "MRT ลาดพร้าว (สำนักงานใหญ่)" },
      { to: "/locations/bts-chit-lom", label: "BTS ชิดลม-เพลินจิต" },
    ],
  },
  {
    icon: Building2, title: "อุตสาหกรรม / กลุ่มลูกค้า",
    links: [
      { to: "/industries", label: "อุตสาหกรรมทั้งหมด" },
      { to: "/industries/healthcare-pharma", label: "โรงพยาบาล / ยา" },
      { to: "/industries/legal-firms", label: "Law Firms" },
      { to: "/industries/fintech-banking", label: "Fintech / Banking" },
      { to: "/industries/hospitality-tourism", label: "Hospitality / Tourism" },
      { to: "/industries/education-academia", label: "Education / Academia" },
      { to: "/industries/tech-saas", label: "Tech / SaaS" },
    ],
  },
  {
    icon: BookOpen, title: "Knowledge Hub & คู่มือ",
    links: [
      { to: "/blog", label: "Knowledge Hub (บทความ)" },
      { to: "/guides", label: "Ultimate Guides 4 คู่มือ" },
      { to: "/case-studies", label: "Case Studies" },
      { to: "/faq", label: "FAQ 50+ ข้อ" },
      { to: "/ask", label: "AI Quick Answers" },
      { to: "/compare", label: "เปรียบเทียบบริการ" },
    ],
  },
  {
    icon: Award, title: "ความน่าเชื่อถือ",
    links: [
      { to: "/trust", label: "Trust Hub" },
      { to: "/reviews", label: "รีวิวลูกค้า 2,147+" },
      { to: "/about/team", label: "ทีม Notary 6 ทนาย" },
      { to: "/pricing", label: "ตารางราคา" },
      { to: "/quote", label: "คำนวณราคาทันที" },
    ],
  },
  {
    icon: Languages, title: "English Site",
    links: [
      { to: "/en", label: "Home (EN)" },
      { to: "/en/services", label: "Services" },
      { to: "/en/pricing", label: "Pricing" },
      { to: "/en/locations", label: "Coverage" },
      { to: "/en/faq", label: "FAQ" },
      { to: "/en/contact", label: "Contact" },
    ],
  },
];

export function InternalSiteMap() {
  return (
    <section aria-label="Site directory" className="border-t border-slate-200 bg-slate-50 py-14 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mb-10 flex items-center gap-3">
          <Compass className="h-5 w-5 text-amber-600" />
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            สำรวจเว็บไซต์ทั้งหมด · 10,000+ หน้าให้บริการ
          </h2>
        </div>
        <div className="grid gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
          {GROUPS.map((g) => {
            const Icon = g.icon;
            return (
              <div key={g.title}>
                <div className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-amber-700">
                  <Icon className="h-3.5 w-3.5" />
                  {g.title}
                </div>
                <ul className="space-y-1.5 text-sm">
                  {g.links.map((l) => (
                    <li key={l.to}>
                      <Link to={l.to} className="text-slate-700 transition hover:text-amber-700 hover:underline">
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
        <div className="mt-10 flex flex-wrap items-center gap-3 border-t border-slate-200 pt-6 text-xs text-slate-500">
          <FileText className="h-3.5 w-3.5" />
          <Link to="/services" className="hover:text-amber-700 hover:underline">บริการทั้งหมด</Link>
          <span>·</span>
          <Link to="/privacy" className="hover:text-amber-700 hover:underline">นโยบายความเป็นส่วนตัว</Link>
          <span>·</span>
          <Link to="/terms" className="hover:text-amber-700 hover:underline">เงื่อนไขการใช้บริการ</Link>
          <span>·</span>
          <HelpCircle className="h-3.5 w-3.5" />
          <Link to="/faq" className="hover:text-amber-700 hover:underline">คำถามที่พบบ่อย</Link>
          <span>·</span>
          <Heart className="h-3.5 w-3.5 text-rose-500" />
          <span>เปิดทำการ จ-ส 9:00–18:00</span>
        </div>
      </div>
    </section>
  );
}
